import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Power, Play, Pause, RefreshCw, Search, Activity, Sparkles, ChevronDown, ChevronRight, CheckCircle2, XCircle, Mic, LayoutDashboard, Database, Users2, ShoppingBag, AlertTriangle, ExternalLink, Ticket, Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import { isLegacyShow } from "@/lib/sports";
import { windowDays, metricsHasData, readinessChecks, readinessOverallOk, emptyStateCopy, fmtDurationMs, pct } from "@/lib/adminMetrics";
import { CONSOLE_SECTIONS, canViewConsole, sanitizeProviderHealth, composeOverviewFromParts, normalizeOverview, orUnknown, NOT_REPORTED, configuredLabel, mergeInterventions } from "@/lib/opsOverview";
import { normalizeVoiceWorker, voiceToggleConfirmed, VOICE_NOT_REPORTED } from "@/lib/voiceWorker";
import { AiDiagnosticsPanel } from "@/components/AiDiagnosticsPanel";
import { buildInviteRegisterLink } from "@/lib/inviteCode";
import { PageHeader, Skeleton } from "@/components/ui-kit";

const DESKTOP_VERSION = process.env.REACT_APP_BUILD || null;

// Two-step confirmation for any mutating / kill-switch action. First click arms;
// a second explicit click within 4s executes. Auto-disarms so nothing fires by
// accident. testid = arm button; `${testid}-confirm` = the executing button.
function ConfirmButton({ onConfirm, children, className = "", testid, confirmLabel = "Confirm", disabled }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (!armed) return; const t = setTimeout(() => setArmed(false), 4000); return () => clearTimeout(t); }, [armed]);
  if (armed) {
    return (
      <span className="inline-flex items-center gap-1">
        <button onClick={() => { setArmed(false); onConfirm(); }} data-testid={`${testid}-confirm`} className={`${className} ring-2 ring-white/70`}>{confirmLabel}?</button>
        <button onClick={() => setArmed(false)} data-testid={`${testid}-cancel`} className="px-2 h-9 rounded-lg text-white/50 text-sm hover:text-white">✕</button>
      </span>
    );
  }
  return <button onClick={() => setArmed(true)} disabled={disabled} data-testid={testid} className={className}>{children}</button>;
}

function Panel({ title, icon: Icon, children, right }) {
  return (
    <section className="tt-panel rounded-xl p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon size={15} className="text-[#4ea152]" /><h2 className="font-display tracking-wide text-white text-sm">{title}</h2></div>
        {right}
      </div>
      {children}
    </section>
  );
}

// Operator review row: full script text/caption/transcript, matchup, generated
// time, strategy version + source snapshot, validation, audio state, and
// preview + gated approve/reject/request-media controls. Approval is BLOCKED
// when validation fails OR the strategy is pre-v4 (stale/legacy) OR killed.
function ScriptItem({ s, onAction }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const validationFailed = s.validation && s.validation.ok === false;
  const legacy = isLegacyShow(s.version);
  const blockReason = s.killed ? "Killed — cannot approve" : validationFailed ? "Blocked — validation failed" : legacy ? `Blocked — legacy strategy v${s.version ?? "?"} (pre-v4)` : null;
  const canApprove = !blockReason && s.state !== "approved" && s.state !== "published";
  const genAt = s.published_at || s.created_at || s.scheduled_at;
  const act = async (action) => { setBusy(action); try { await onAction(s.id, action); } finally { setBusy(""); } };
  return (
    <div className="border-b border-[#2A3544] last:border-0" data-testid={`script-row-${s.id}`}>
      <div className="flex items-center gap-3 py-2.5">
        <button onClick={() => setOpen((o) => !o)} className="text-white/40 hover:text-white shrink-0" data-testid={`script-expand-${s.id}`}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button>
        <span className="text-sm text-white flex-1 truncate">
          <span className="text-white/50">{s.game_id}</span> · {s.character_id} · {s.segment_type || s.episode_type || s.type}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${legacy ? "bg-[#3a2f14] text-[#F4B740]" : "bg-white/10 text-white/60"}`} data-testid={`script-version-${s.id}`}>v{s.version ?? "?"}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${validationFailed ? "bg-[#3a1414] text-[#ff8a80]" : "bg-[#14361f] text-[#4ea152]"}`} data-testid={`script-validation-${s.id}`}>
          {validationFailed ? <XCircle size={11} /> : <CheckCircle2 size={11} />}{validationFailed ? "invalid" : "valid"}
        </span>
        <span className="text-[11px] px-2 py-0.5 rounded bg-white/10 text-white/60">{s.state}</span>
      </div>
      {open && (
        <div className="pb-3 pl-8 pr-1 space-y-2" data-testid={`script-detail-${s.id}`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <div><div className="text-white/35">STRATEGY</div><div className="text-white">v{s.version ?? "?"}{legacy ? " · legacy" : ""}</div></div>
            <div><div className="text-white/35">SOURCE SNAPSHOT</div><div className="text-white truncate" title={s.snapshot_id || ""}>{s.snapshot_id ? s.snapshot_id.slice(0, 12) + "…" : "—"}</div></div>
            <div><div className="text-white/35">GENERATED</div><div className="text-white">{genAt ? new Date(genAt).toLocaleString() : "—"}</div></div>
            <div><div className="text-white/35">AUDIO</div><div className="text-white">{s.media_available ? "available" : "none"}</div></div>
          </div>
          <div>
            <div className="text-white/35 text-[11px] mb-1">SCRIPT</div>
            <p className="text-sm text-white/85 bg-black/25 rounded-lg p-2.5 leading-snug" data-testid={`script-text-${s.id}`}>{s.text || "—"}</p>
          </div>
          {s.caption && <div><div className="text-white/35 text-[11px] mb-1">CAPTION</div><p className="text-sm text-white/70">{s.caption}</p></div>}
          {s.transcript && <div><div className="text-white/35 text-[11px] mb-1">TRANSCRIPT</div><p className="text-sm text-white/70 whitespace-pre-wrap">{s.transcript}</p></div>}
          {Array.isArray(s.claims) && s.claims.length > 0 && (
            <div><div className="text-white/35 text-[11px] mb-1">FACT CLAIMS ({s.claims.length})</div>
              <ul className="text-xs text-white/70 list-disc pl-4 space-y-0.5">{s.claims.slice(0, 8).map((c, i) => <li key={i}>{typeof c === "string" ? c : (c.text || c.claim || JSON.stringify(c))}</li>)}</ul>
            </div>
          )}
          {blockReason && <div className="text-[11px] text-[#ff8a80]" data-testid={`script-block-${s.id}`}>{blockReason}</div>}
          <div className="flex flex-wrap gap-2 pt-1">
            <ConfirmButton onConfirm={() => act("approve")} disabled={!canApprove || !!busy} testid={`script-approve-${s.id}`} className="text-xs px-3 h-8 rounded-lg bg-[#2F7D32] text-white disabled:opacity-40 inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} /> Approve</span></ConfirmButton>
            <ConfirmButton onConfirm={() => act("reject")} disabled={!!busy || s.state === "published"} testid={`script-reject-${s.id}`} className="text-xs px-3 h-8 rounded-lg bg-[#3a1414] text-[#ff8a80] disabled:opacity-40 inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><XCircle size={13} /> Reject</span></ConfirmButton>
            <ConfirmButton onConfirm={() => act("request-media")} disabled={!!busy} testid={`script-requestmedia-${s.id}`} className="text-xs px-3 h-8 rounded-lg tt-panel-3 text-white disabled:opacity-40 inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><Mic size={13} /> Request media</span></ConfirmButton>
          </div>
        </div>
      )}
    </div>
  );
}

// Acquisition-proof panel — first-party metrics + operations readiness. Honest
// by construction: zero engagement is never framed as traction/health.
function Stat({ label, value, sub, tid }) {
  return (
    <div className="rounded-lg bg-black/25 border border-white/10 p-3" data-testid={tid}>
      <div className="text-[10px] tracking-wide text-white/40 uppercase">{label}</div>
      <div className="font-display text-xl text-white tabnums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}

function AcquisitionPanel() {
  const [window, setWindow] = useState("7d");
  const [metrics, setMetrics] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      TurfTalk.adminTelemetryMetrics(window).catch(() => null),
      TurfTalk.adminOpsReadiness().catch(() => null),
    ]).then(([m, r]) => { if (alive) { setMetrics(m); setReadiness(r); setLoading(false); } });
    return () => { alive = false; };
  }, [window]);

  const hasData = metricsHasData(metrics);
  const rows = readinessChecks(readiness);

  return (
    <Panel title="ACQUISITION PROOF · TELEMETRY + READINESS" icon={Activity}
      right={
        <div className="flex gap-1" data-testid="metrics-window-toggle">
          {["7d", "30d"].map((w) => (
            <button key={w} onClick={() => setWindow(w)} data-testid={`metrics-window-${w}`}
              className={`px-3 h-8 rounded-lg text-xs font-display tracking-wide transition-colors ${window === w ? "bg-[#2F7D32] text-white" : "tt-panel-3 text-white/60 hover:text-white"}`}>{w.toUpperCase()}</button>
          ))}
        </div>
      }>
      {loading ? <Skeleton className="h-40" /> : (
        <div data-testid="acquisition-panel">
          <div className="text-[11px] text-white/40 mb-3">Last {windowDays(window)} days · {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleString() : "—"}</div>

          {!hasData && (
            <div className="mb-4 rounded-lg border border-[#3a2f14] bg-[#241d0e] px-3 py-2.5 text-[12px] text-[#F4B740]" data-testid="metrics-empty-state">
              {emptyStateCopy(metrics)}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            <Stat tid="stat-registered" label="Registered" value={metrics?.users?.registered ?? "—"} sub={`${metrics?.users?.verified ?? 0} verified`} />
            <Stat tid="stat-dau" label="DAU" value={metrics?.dau ?? 0} />
            <Stat tid="stat-wau" label="WAU" value={metrics?.wau ?? 0} />
            <Stat tid="stat-peak" label="Peak concurrent" value={metrics?.peak_concurrent_sessions?.peak ?? 0} sub={`${metrics?.peak_concurrent_sessions?.window_minutes ?? 5}m window`} />
            <Stat tid="stat-avg-session" label="Avg game session" value={fmtDurationMs(metrics?.avg_game_window_session_ms)} />
            <Stat tid="stat-return" label="2nd-game return" value={metrics?.returning_second_game_window?.returning ?? 0} sub={`${pct(metrics?.returning_second_game_window?.rate)} of ${metrics?.returning_second_game_window?.viewers ?? 0}`} />
            <Stat tid="stat-rooms" label="Active rooms" value={metrics?.rooms?.active_rooms ?? 0} sub={`${metrics?.rooms?.room_joins ?? 0} joins`} />
            <Stat tid="stat-picks" label="Picks submitted" value={metrics?.picks_submitted ?? 0} />
            <Stat tid="stat-show" label="Show starts" value={metrics?.character_show?.starts ?? 0} sub={`${metrics?.character_show?.completions ?? 0} done · ${pct(metrics?.character_show?.completion_rate)}`} />
            <Stat tid="stat-hosts" label="Hosts" value={metrics?.hosts?.distinct ?? 0} sub={`${metrics?.hosts?.repeat ?? 0} repeat`} />
            <Stat tid="stat-surface" label="Web / Mobile" value={`${metrics?.surface?.web ?? 0} / ${metrics?.surface?.mobile ?? 0}`} />
          </div>

          <div className="mt-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-display tracking-widest text-xs text-white/50">OPERATIONS READINESS</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${readinessOverallOk(readiness) ? "bg-[#14361f] text-[#4ea152]" : "bg-[#3a1414] text-[#ff8a80]"}`} data-testid="readiness-overall">{readinessOverallOk(readiness) ? "READY" : "ATTENTION"}</span>
            </div>
            {rows.length === 0 ? <p className="text-white/40 text-sm">Readiness unavailable.</p> : (
              <div className="space-y-1.5" data-testid="readiness-rows">
                {rows.map((r) => (
                  <div key={r.key} className="flex items-center gap-2 text-sm" data-testid={`readiness-${r.key}`}>
                    {r.ok ? <CheckCircle2 size={14} className="text-[#4ea152] shrink-0" /> : <XCircle size={14} className="text-[#ff8a80] shrink-0" />}
                    <span className="text-white/70 w-44 shrink-0">{r.label}</span>
                    <span className="text-white/50 truncate">{r.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

// ── Overview section ────────────────────────────────────────────────────────
function HealthPill({ state }) {
  const map = { healthy: ["bg-[#14361f]", "text-[#4ea152]", "HEALTHY"], attention: ["bg-[#3a2f14]", "text-[#F4B740]", "NEEDS ATTENTION"], unknown: ["bg-white/10", "text-white/60", "UNKNOWN"] };
  const [bg, fg, label] = map[state] || map.unknown;
  return <span className={`text-[11px] font-display tracking-wide px-2 py-0.5 rounded ${bg} ${fg}`} data-testid="overview-health">{label}</span>;
}

function SubsystemDot({ s }) {
  const good = s.state === "active";
  const bad = s.state === "killed" || s.state === "degraded" || s.state === "paused";
  const color = good ? "text-[#4ea152]" : bad ? "text-[#ff8a80]" : "text-white/40";
  return (
    <div className="rounded-lg bg-black/25 border border-white/10 p-3" data-testid={`overview-subsystem-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] tracking-wide text-white/40 uppercase">{s.label}</div>
      <div className={`text-sm mt-0.5 capitalize ${color}`}>{s.state}</div>
    </div>
  );
}

// ── Subsystem monitoring (overview.monitoring, schema_version:1) ──────────────
const MONITOR_STATE_MAP = {
  green: ["text-[#4ea152]", "OPERATIONAL"],
  amber: ["text-[#F4B740]", "DEGRADED"],
  red: ["text-[#ff8a80]", "DOWN"],
  inactive: ["text-white/40", "INACTIVE"],
  unverified: ["text-white/40", "UNVERIFIED"],
};
function MonitorState({ state }) {
  const [fg, label] = MONITOR_STATE_MAP[state] || MONITOR_STATE_MAP.unverified;
  return <span className={`text-[10px] font-display tracking-wide ${fg}`} data-testid="monitor-state">{label}</span>;
}
function MonitorRow({ s }) {
  const fmt = (t) => (t ? new Date(t).toLocaleString() : NOT_REPORTED);
  return (
    <div className="rounded-lg bg-black/25 border border-white/10 p-3" data-testid={`monitor-subsystem-${s.name}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-white">{s.label}</span>
        <MonitorState state={s.state} />
      </div>
      <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-0.5 text-[11px] text-white/50">
        <span data-testid={`monitor-${s.name}-last-success`}>Last success: {fmt(s.lastSuccess)}</span>
        <span data-testid={`monitor-${s.name}-last-sync`}>Last sync: {fmt(s.lastSync)}</span>
        <span data-testid={`monitor-${s.name}-last-failure`}>Last failure: {fmt(s.lastFailure)}</span>
      </div>
      {s.failureReason && <p className="text-[11px] text-[#ff8a80] mt-1" data-testid={`monitor-${s.name}-reason`}>{s.failureReason}</p>}
      {s.recommendedAction && <p className="text-[11px] text-[#F4B740] mt-0.5" data-testid={`monitor-${s.name}-action`}>Action: {s.recommendedAction}</p>}
      {s.note && <p className="text-[11px] text-white/40 mt-0.5">{s.note}</p>}
    </div>
  );
}

function OverviewSection({ overview, loading, onRefresh }) {
  if (loading || !overview) return <Skeleton className="h-64" />;
  const o = overview;
  const mergedInterventions = mergeInterventions(o.interventions, o.monitoring);
  return (
    <div data-testid="overview-section" className="space-y-5">
      <Panel title="PRODUCTION HEALTH" icon={LayoutDashboard} right={<button onClick={onRefresh} className="text-white/40 hover:text-white" data-testid="overview-refresh"><RefreshCw size={14} /></button>}>
        <div className="flex items-center gap-3 flex-wrap">
          <HealthPill state={o.health.state} />
          <span className="text-sm text-white/70">{o.health.summary}</span>
          <span className="ml-auto text-[10px] text-white/35">source: {o.source}{o.generatedAt ? ` · ${new Date(o.generatedAt).toLocaleString()}` : ""}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-3">
          <Stat tid="overview-ver-backend" label="Backend version" value={o.versions.backend.value || NOT_REPORTED} sub={o.versions.backend.reason} />
          <Stat tid="overview-ver-desktop" label="Desktop build" value={o.versions.desktop.value || NOT_REPORTED} sub={o.versions.desktop.reason} />
          <Stat tid="overview-ver-mobile" label="Mobile version" value={o.versions.mobile.value || NOT_REPORTED} sub={o.versions.mobile.reason} />
        </div>
      </Panel>

      <Panel title="SPORTS DATA (BALLDONTLIE)" icon={Database}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="overview-provider">
          <Stat tid="overview-provider-name" label="Provider" value={o.provider.name || NOT_REPORTED} />
          <Stat tid="overview-provider-configured" label="Configured" value={configuredLabel(o.provider.configured)} />
          <Stat tid="overview-provider-refreshing" label="Refreshing" value={o.provider.refreshing ? "Yes" : o.provider.offline ? "Offline" : o.provider.error ? "Error" : o.provider.stale ? "Stale" : NOT_REPORTED} />
          <Stat tid="overview-provider-refresh" label="Last refresh" value={o.provider.lastRefresh ? new Date(o.provider.lastRefresh).toLocaleString() : NOT_REPORTED} />
        </div>
        {o.provider.quotaNote && (
          <p className="text-[11px] text-white/45 mt-2" data-testid="overview-provider-quota-note">{o.provider.quotaNote}</p>
        )}
        {o.provider.refreshActivity && (
          <p className="text-[11px] text-white/45 mt-1" data-testid="overview-refresh-activity">{o.provider.refreshActivity}.</p>
        )}
      </Panel>

      <Panel title="SUBSYSTEMS" icon={Activity}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <SubsystemDot s={o.subsystems.roomAi} />
          <SubsystemDot s={o.subsystems.predictions} />
          <SubsystemDot s={o.subsystems.characterShows} />
          <SubsystemDot s={o.subsystems.voices} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="overview-killswitches">
          {o.killSwitches.map((k) => (
            <span key={k.key} className={`text-[11px] px-2 py-1 rounded ${k.active ? "bg-[#3a1414] text-[#ff8a80]" : "bg-white/10 text-white/50"}`} data-testid={`overview-kill-${k.key}`}>
              {k.label}: {k.active ? "Engaged" : "Not engaged"}
            </span>
          ))}
        </div>
      </Panel>

      <Panel title="SUBSYSTEM MONITORING" icon={Activity}>
        {!o.monitoring || !o.monitoring.present ? (
          <p className="text-sm text-white/50" data-testid="monitoring-not-reported">Subsystem monitoring is {NOT_REPORTED.toLowerCase()} by this backend.</p>
        ) : (
          <div data-testid="monitoring-section" className="space-y-2.5">
            <div className="flex items-center gap-3 flex-wrap">
              <MonitorState state={o.monitoring.summary.overall} />
              {o.monitoring.summary.note && <span className="text-sm text-white/70" data-testid="monitoring-summary-note">{o.monitoring.summary.note}</span>}
              {o.monitoring.generatedAt && <span className="ml-auto text-[10px] text-white/35">observed: {new Date(o.monitoring.generatedAt).toLocaleString()}</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {o.monitoring.subsystems.map((s) => <MonitorRow key={s.name} s={s} />)}
            </div>
          </div>
        )}
      </Panel>

      <Panel title="NEEDS INTERVENTION NOW" icon={AlertTriangle}>
        {mergedInterventions.length === 0
          ? <p className="text-sm text-[#4ea152]" data-testid="overview-interventions-none">Nothing requires operator intervention right now.</p>
          : <ul className="space-y-1.5" data-testid="overview-interventions">{mergedInterventions.map((i, idx) => <li key={idx} className="text-sm text-[#F4B740] flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {i}</li>)}</ul>}
      </Panel>
    </div>
  );
}

function PlayersSection({ overview, loading }) {
  if (loading || !overview) return <Skeleton className="h-40" />;
  const p = overview.players;
  const Card = ({ league, d, tid }) => (
    <div className="tt-panel rounded-xl p-4" data-testid={tid}>
      <div className="flex items-center justify-between mb-2"><span className="font-display text-white">{league}</span><span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/60">{d.state}</span></div>
      <div className="text-sm text-white/70">Verified players: <span className="text-white">{d.count ?? orUnknown(null)}</span></div>
      <div className="text-sm text-white/70">Coverage: <span className="text-white">{d.coverage ?? orUnknown(null)}</span></div>
      <div className="text-[11px] text-white/40 mt-1">Last refresh: {d.lastRefresh ? new Date(d.lastRefresh).toLocaleString() : orUnknown(null)}</div>
    </div>
  );
  return (
    <div data-testid="players-section" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <Card league="NFL" d={p.nfl} tid="players-nfl" />
        <Card league="NCAA" d={p.ncaa} tid="players-ncaa" />
      </div>
      <div className="rounded-lg border border-[#3a2f14] bg-[#241d0e] px-3 py-2.5 text-[12px] text-[#F4B740]" data-testid="players-ncaaf-note">{p.note}</div>
      <p className="text-[11px] text-white/40">Per-player detail opens from any team roster, stat-leader, or box-score row (see a team page). Player data is current-season only.</p>
    </div>
  );
}

const INVITE_STATUS = {
  active: ["bg-[#14361f]", "text-[#4ea152]"],
  expired: ["bg-[#3a2f14]", "text-[#F4B740]"],
  exhausted: ["bg-[#3a2f14]", "text-[#F4B740]"],
  revoked: ["bg-[#3a1414]", "text-[#ff8a80]"],
};

// Admin-only beta invitation control. Create a single-use, short-expiry invite
// (backend generates the random code + enforces authorization); the raw code is
// returned exactly once. We show it + the registration link for one-time copy,
// never persist/log it, and support revocation. No real codes are created here
// unless an operator explicitly clicks through the two-step confirm.
export function BetaInvitesPanel() {
  const [codes, setCodes] = useState(null);
  const [label, setLabel] = useState("");
  const [capacity, setCapacity] = useState(1);
  const [days, setDays] = useState(7);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => TurfTalk.adminBetaCodes().then((r) => setCodes(r.codes || [])).catch(() => setCodes([])),
    []
  );
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (busy) return;
    if (!label.trim()) { toast.error("Add a label so you can identify this invite."); return; }
    setBusy(true);
    try {
      const cap = Math.max(1, Number(capacity) || 1);
      const d = Number(days) || 0;
      const body = { label: label.trim(), capacity: cap };
      if (d > 0) body.expires_in_days = d;
      const res = await TurfTalk.adminCreateBetaCode(body);
      const link = buildInviteRegisterLink(window.location.origin, res.raw);
      setCreated({ raw: res.raw, link, label: res.label });
      setLabel("");
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await TurfTalk.adminRevokeBetaCode(id); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const copy = (text, what) => {
    try { navigator.clipboard?.writeText(text); } catch { /* noop */ }
    toast.success(`${what} copied`);
  };

  return (
    <Panel title="BETA INVITES" icon={Ticket} right={<button onClick={load} className="text-white/40 hover:text-white" data-testid="beta-invites-refresh"><RefreshCw size={14} /></button>}>
      <div data-testid="beta-invites-panel">
        <p className="text-[11px] text-white/40 mb-3">Create a single-use invite with a short expiry. The code is generated by the backend and shown once — copy the registration link and send it to your invitee. Revoke disables it immediately.</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <label className="block">
            <span className="block text-[11px] text-white/50 mb-1">Label</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Coach Dave" data-testid="beta-invite-label" className="w-full h-10 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
          </label>
          <label className="block">
            <span className="block text-[11px] text-white/50 mb-1">Uses</span>
            <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="beta-invite-capacity" className="w-20 h-10 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
          </label>
          <label className="block">
            <span className="block text-[11px] text-white/50 mb-1">Expires (days)</span>
            <input type="number" min={0} value={days} onChange={(e) => setDays(e.target.value)} data-testid="beta-invite-days" className="w-24 h-10 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
          </label>
          <ConfirmButton onConfirm={create} disabled={busy} testid="beta-invite-create" className="px-4 h-10 rounded-lg bg-[#2F7D32] text-white text-sm disabled:opacity-40">Create invite</ConfirmButton>
        </div>

        {created && (
          <div className="mt-4 rounded-lg border border-[#24362A] bg-[#122017] p-3" data-testid="beta-invite-created">
            <div className="text-[11px] text-[#4ea152] mb-2">Invite created for “{created.label}”. Copy it now — it will not be shown again.</div>
            <div className="flex items-center gap-2 mb-2">
              <code className="flex-1 truncate text-xs text-white bg-black/30 rounded px-2 py-1.5" data-testid="beta-invite-raw">{created.raw}</code>
              <button onClick={() => copy(created.raw, "Code")} data-testid="beta-invite-copy-code" className="px-2 h-8 rounded-lg tt-panel-3 text-white/80 text-xs inline-flex items-center gap-1"><Copy size={12} /> Code</button>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate text-xs text-white/80 bg-black/30 rounded px-2 py-1.5" data-testid="beta-invite-link">{created.link}</code>
              <button onClick={() => copy(created.link, "Link")} data-testid="beta-invite-copy-link" className="px-2 h-8 rounded-lg bg-[#2F7D32] text-white text-xs inline-flex items-center gap-1"><Copy size={12} /> Link</button>
            </div>
          </div>
        )}

        <div className="mt-4">
          {!codes ? <Skeleton className="h-16" /> : codes.length === 0 ? (
            <p className="text-white/40 text-sm" data-testid="beta-invites-empty">No invites yet.</p>
          ) : (
            <div className="space-y-1.5" data-testid="beta-invites-list">
              {codes.map((c) => {
                const [bg, fg] = INVITE_STATUS[c.status] || ["bg-white/10", "text-white/60"];
                const active = c.status === "active";
                return (
                  <div key={c.id} className="flex items-center gap-3 py-2 border-b border-[#2A3544] last:border-0 text-sm" data-testid={`beta-invite-row-${c.id}`}>
                    <span className="text-white flex-1 truncate">{c.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${bg} ${fg}`} data-testid={`beta-invite-status-${c.id}`}>{c.status}</span>
                    <span className="text-white/50 text-xs whitespace-nowrap">{c.remaining}/{c.capacity} left</span>
                    <span className="text-white/35 text-xs whitespace-nowrap">{c.expires_at ? `exp ${new Date(c.expires_at).toLocaleDateString()}` : "no expiry"}</span>
                    {active && <ConfirmButton onConfirm={() => revoke(c.id)} testid={`beta-invite-revoke-${c.id}`} className="px-2 h-8 rounded-lg bg-[#3a1414] text-[#ff8a80] text-xs inline-flex items-center gap-1"><span className="inline-flex items-center gap-1"><Trash2 size={12} /> Revoke</span></ConfirmButton>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [section, setSection] = useState("overview");
  const [scheduler, setScheduler] = useState(null);
  const [scripts, setScripts] = useState(null);
  const [providers, setProviders] = useState(null);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [aiCfg, setAiCfg] = useState(null);
  const [voices, setVoices] = useState(null);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [overview, setOverview] = useState(null);
  const [ovLoading, setOvLoading] = useState(true);

  const loadScheduler = useCallback(() => TurfTalk.schedulerStatus().then(setScheduler).catch(() => setScheduler({})), []);
  const loadScripts = useCallback(() => TurfTalk.adminCharacterScripts({}).then((r) => setScripts(r.scripts || r.items || [])).catch(() => setScripts([])), []);
  const loadAi = useCallback(() => TurfTalk.adminAiConfig().then(setAiCfg).catch(() => setAiCfg(null)), []);
  const loadVoices = useCallback(() => TurfTalk.adminVoiceWorker().then((r) => setVoices(normalizeVoiceWorker(r))).catch(() => setVoices(null)), []);

  // Overview adapter: consume the shared /admin/ops/overview if published; else
  // compose an honest overview from the live admin sub-endpoints.
  const loadOverview = useCallback(async () => {
    setOvLoading(true);
    const [readiness, aiConfig, prov, sched] = await Promise.all([
      TurfTalk.adminOpsReadiness().catch(() => null),
      TurfTalk.adminAiConfig().catch(() => null),
      TurfTalk.adminProviderHealth().catch(() => null),
      TurfTalk.schedulerStatus().catch(() => null),
    ]);
    let vm = null;
    try { vm = normalizeOverview(await TurfTalk.adminOpsOverview(), { desktopVersion: DESKTOP_VERSION }); }
    catch (e) { if (e instanceof AuthExpiredError) { setOvLoading(false); return; } }
    if (!vm) vm = composeOverviewFromParts({ readiness, aiConfig, providers: prov, scheduler: sched, desktopVersion: DESKTOP_VERSION });
    setOverview(vm);
    setOvLoading(false);
  }, []);

  const safeProviders = useMemo(() => (providers ? sanitizeProviderHealth(providers) : null), [providers]);

  useEffect(() => {
    if (!canViewConsole(user)) return;
    loadScheduler(); loadScripts(); loadAi(); loadVoices(); loadOverview();
    TurfTalk.adminProviderHealth().then(setProviders).catch(() => setProviders({}));
  }, [user, loadScheduler, loadScripts, loadAi, loadVoices, loadOverview]);

  if (!canViewConsole(user)) {
    return <div className="max-w-3xl mx-auto px-8 py-20 text-center text-white/50" data-testid="console-access-denied">Operator access required.</div>;
  }

  const kill = async (on) => { try { await TurfTalk.adminGlobalKill(on); toast.success(`Character shows ${on ? "killed" : "enabled"} globally`); loadScheduler(); loadOverview(); } catch (e) { toast.error(e.message); } };
  const sched = async (fn, msg) => { try { await fn(); toast.success(msg); loadScheduler(); loadScripts(); loadOverview(); } catch (e) { toast.error(e.message); } };
  const scriptAction = async (id, action) => { try { await TurfTalk.adminScriptAction(id, action); toast.success(`${action} applied`); loadScripts(); } catch (e) { toast.error(e.message); } };
  const search = async () => { try { const r = await TurfTalk.adminUsers({ handle: q, email: q.includes("@") ? q : undefined }); setUsers(r.users || []); } catch (e) { toast.error(e.message); } };
  const aiKill = async () => { try { await TurfTalk.adminAiKill(); toast.success("AI engagement killed"); loadAi(); loadOverview(); } catch (e) { toast.error(e.message); } };
  const aiResume = async () => { try { await TurfTalk.adminAiResume(); toast.success("AI engagement resumed"); loadAi(); loadOverview(); } catch (e) { toast.error(e.message); } };
  const voiceToggle = async (on) => {
    if (voiceBusy) return;
    setVoiceBusy(true);
    try {
      await TurfTalk.adminVoiceWorkerSetEnabled(on);
      const vm = normalizeVoiceWorker(await TurfTalk.adminVoiceWorker());
      setVoices(vm);
      if (voiceToggleConfirmed(vm, on)) {
        toast.success(`Audio worker ${on ? "started" : "paused"}`);
        loadOverview();
      } else {
        toast.error("Toggle not confirmed — backend did not report the new state. Refresh and retry.");
      }
    } catch (e) {
      toast.error(e.message);
      loadVoices();
    } finally {
      setVoiceBusy(false);
    }
  };

  const aiKilled = !!aiCfg?.kill_switch;
  const killed = scheduler?.kill_switch || scheduler?.global_kill || false;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
      <PageHeader title="Operator Console" subtitle="Unified command center for the Turf Talk project — read-only by default." right={<Shield className="text-[#4ea152]" />} />

      <div className="flex gap-1.5 overflow-x-auto tt-scroll mb-6 -mx-1 px-1 sticky top-0 md:top-12 z-20 py-2 bg-[#10151D]/90 backdrop-blur" role="tablist" data-testid="console-tabs">
        {CONSOLE_SECTIONS.map((s) => (
          <button key={s.key} role="tab" aria-selected={section === s.key} onClick={() => setSection(s.key)} data-testid={`console-tab-${s.key}`}
            className={`px-3.5 h-9 rounded-full text-sm whitespace-nowrap font-display tracking-wide transition-colors ${section === s.key ? "bg-[#2F7D32] text-white" : "tt-panel text-white/55 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {section === "overview" && <OverviewSection overview={overview} loading={ovLoading} onRefresh={loadOverview} />}

      {section === "data" && (
        <Panel title="PROVIDER HEALTH" icon={Database} right={<button onClick={() => TurfTalk.adminProviderHealth().then(setProviders).catch(() => setProviders({}))} className="text-white/40 hover:text-white"><RefreshCw size={14} /></button>}>
          {!safeProviders ? <Skeleton className="h-24" /> : (
            <pre className="text-xs text-white/60 overflow-x-auto tt-scroll" data-testid="provider-health-json">{JSON.stringify(safeProviders, null, 2)}</pre>
          )}
          <p className="text-[11px] text-white/35 mt-2">Read-only. Secret-like fields are hidden. Data & sync refresh actions surface here only when the backend exposes bounded provider controls.</p>
        </Panel>
      )}

      {section === "players" && <PlayersSection overview={overview} loading={ovLoading} />}

      {section === "ai" && (
        <Panel title="AI ENGAGEMENT — KILL SWITCH" icon={Sparkles} right={<button onClick={loadAi} className="text-white/40 hover:text-white"><RefreshCw size={14} /></button>}>
          {!aiCfg ? <p className="text-white/40 text-sm">AI config unavailable.</p> : (
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`text-sm ${aiKilled ? "text-[#ff6b60]" : (aiCfg.enabled ? "text-[#4ea152]" : "text-white/50")}`} data-testid="admin-ai-state">
                {aiKilled ? "KILLED — no agents chime in" : aiCfg.enabled ? "ACTIVE — agents may chime in" : "DISABLED"}
              </span>
              <span className="text-[11px] text-white/40">cooldown {aiCfg.cooldown_seconds}s · rate {aiCfg.rate_per_hour}/hr · flag {String(aiCfg.feature_flag)}</span>
              <div className="ml-auto flex gap-2">
                <ConfirmButton onConfirm={aiKill} testid="ai-kill-on" className="px-3 h-9 rounded-lg bg-[#D92D20] text-white text-sm">Kill</ConfirmButton>
                <ConfirmButton onConfirm={aiResume} testid="ai-kill-off" className="px-3 h-9 rounded-lg bg-[#2F7D32] text-white text-sm">Resume</ConfirmButton>
              </div>
            </div>
          )}
        </Panel>
      )}
      {section === "ai" && <AiDiagnosticsPanel canView={canViewConsole(user)} />}

      {section === "shows" && (
        <>
          <Panel title="CHARACTER SHOW — GLOBAL KILL SWITCH" icon={Power}>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${killed ? "text-[#ff6b60]" : "text-[#4ea152]"}`}>{killed ? "KILLED — no episodes render" : "ACTIVE — episodes render normally"}</span>
              <div className="ml-auto flex gap-2">
                <ConfirmButton onConfirm={() => kill(true)} testid="kill-on" className="px-3 h-9 rounded-lg bg-[#D92D20] text-white text-sm">Kill all</ConfirmButton>
                <ConfirmButton onConfirm={() => kill(false)} testid="kill-off" className="px-3 h-9 rounded-lg bg-[#2F7D32] text-white text-sm">Enable</ConfirmButton>
              </div>
            </div>
          </Panel>

          <Panel title="CHARACTER SHOW — AUDIO WORKER" icon={Mic} right={<button onClick={loadVoices} className="text-white/40 hover:text-white" data-testid="voice-worker-refresh"><RefreshCw size={14} /></button>}>
            {!voices ? <Skeleton className="h-16" /> : (
              <div className="space-y-3" data-testid="voice-worker-panel">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    data-testid="voice-worker-state"
                    className={`text-sm ${voices.state === "active" ? "text-[#4ea152]" : voices.state === "killed" ? "text-[#ff6b60]" : "text-white/55"}`}
                  >
                    {voices.state === "active"
                      ? "ON — branded audio generates automatically"
                      : voices.state === "paused"
                      ? "PAUSED — no new audio generates"
                      : voices.state === "killed"
                      ? "PROVIDER KILLED — audio generation blocked"
                      : "State not reported"}
                  </span>
                  {voices.providerKill === true && (
                    <span className="text-[11px] text-[#ff6b60]" data-testid="voice-worker-provider-kill">
                      Provider kill switch is active — starting the worker will not produce audio until it is cleared.
                    </span>
                  )}
                  {voices.providerConfigured === false && (
                    <span className="text-[11px] text-[#F4B740]" data-testid="voice-worker-provider-unconfigured">
                      Provider not configured — audio cannot generate yet.
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    <ConfirmButton onConfirm={() => voiceToggle(true)} testid="voice-start" disabled={voiceBusy || voices.enabled === true} className="px-3 h-9 rounded-lg bg-[#2F7D32] text-white text-sm disabled:opacity-40">
                      <span className="inline-flex items-center gap-1.5"><Play size={14} /> Start</span>
                    </ConfirmButton>
                    <ConfirmButton onConfirm={() => voiceToggle(false)} testid="voice-pause" disabled={voiceBusy || voices.enabled === false} className="px-3 h-9 rounded-lg tt-panel-3 text-white text-sm disabled:opacity-40">
                      <span className="inline-flex items-center gap-1.5"><Pause size={14} /> Pause</span>
                    </ConfirmButton>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
                  <div className="tt-panel-3 rounded-lg px-3 py-2" data-testid="voice-worker-running">
                    <div className="text-white/40">Worker running</div>
                    <div className="text-white">{voices.running === null ? VOICE_NOT_REPORTED : voices.running ? "Yes" : "No"}</div>
                  </div>
                  <div className="tt-panel-3 rounded-lg px-3 py-2" data-testid="voice-worker-autogen">
                    <div className="text-white/40">Auto pregame</div>
                    <div className="text-white">{voices.autogenNewPregame === null ? VOICE_NOT_REPORTED : voices.autogenNewPregame ? "On" : "Off"}</div>
                  </div>
                  <div className="tt-panel-3 rounded-lg px-3 py-2" data-testid="voice-worker-usage">
                    <div className="text-white/40">Daily usage / cap</div>
                    <div className="text-white">
                      {voices.dailyUsed === null ? VOICE_NOT_REPORTED : voices.dailyUsed.toLocaleString()}
                      {" / "}
                      {voices.dailyCap === null ? VOICE_NOT_REPORTED : voices.dailyCap.toLocaleString()}
                    </div>
                  </div>
                  <div className="tt-panel-3 rounded-lg px-3 py-2" data-testid="voice-worker-last-error">
                    <div className="text-white/40">Last provider error</div>
                    <div className="text-white">{voices.lastError ? voices.lastError.category : "None"}</div>
                    <div className="text-white/40">{voices.lastError?.at ? new Date(voices.lastError.at).toLocaleString() : ""}</div>
                  </div>
                </div>
                <p className="text-[11px] text-white/35">Start/Pause only flips the worker on or off; the daily character cap and automatic-pregame preference are preserved untouched. Audio runs on the normal worker cadence — there is no manual generate or preview here.</p>
              </div>
            )}
          </Panel>

          <Panel title="WEEKLY SLATE SCHEDULER" icon={Activity} right={<button onClick={loadScheduler} className="text-white/40 hover:text-white"><RefreshCw size={14} /></button>}>
            {!scheduler ? <Skeleton className="h-12" /> : (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-white/70">State: <span className="text-white">{scheduler.state || scheduler.status || (scheduler.paused ? "paused" : "running")}</span></span>
                <div className="ml-auto flex gap-2">
                  <ConfirmButton onConfirm={() => sched(TurfTalk.schedulerRunOnce, "Ran once")} testid="scheduler-run-once" className="px-3 h-9 rounded-lg tt-panel-3 text-white text-sm inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><Play size={14} /> Run once</span></ConfirmButton>
                  <ConfirmButton onConfirm={() => sched(TurfTalk.schedulerPause, "Paused")} testid="scheduler-pause" className="px-3 h-9 rounded-lg tt-panel-3 text-white text-sm inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><Pause size={14} /> Pause</span></ConfirmButton>
                  <ConfirmButton onConfirm={() => sched(TurfTalk.schedulerResume, "Resumed")} testid="scheduler-resume" className="px-3 h-9 rounded-lg tt-panel-3 text-white text-sm inline-flex items-center gap-1.5"><span className="inline-flex items-center gap-1.5"><Play size={14} /> Resume</span></ConfirmButton>
                </div>
              </div>
            )}
          </Panel>

          <Panel title="EPISODE SCRIPTS · OPERATOR REVIEW" icon={Activity} right={<button onClick={loadScripts} className="text-white/40 hover:text-white"><RefreshCw size={14} /></button>}>
            {!scripts ? <Skeleton className="h-20" /> : scripts.length === 0 ? <p className="text-white/40 text-sm">No scripts found.</p> : (
              <div>
                <p className="text-[11px] text-white/40 mb-2">Expand a row to preview the full script, picks, matchup, source snapshot, validation and audio state before approving. Approval is blocked when validation fails or the strategy is pre-v4; every action requires confirmation.</p>
                {scripts.slice(0, 25).map((s) => <ScriptItem key={s.id} s={s} onAction={scriptAction} />)}
              </div>
            )}
          </Panel>
        </>
      )}

      {section === "users" && (
        <>
          <BetaInvitesPanel />
          <Panel title="ACCOUNT LOOKUP" icon={Search}>
            <div className="flex gap-2 mb-3">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="handle or email" data-testid="admin-user-q" className="flex-1 h-10 bg-[#10151D] border border-[#2A3544] rounded-lg px-3 text-sm text-white outline-none focus:border-[#2F7D32]" />
              <button onClick={search} data-testid="admin-user-search" className="px-4 h-10 rounded-lg bg-[#2F7D32] text-white text-sm">Search</button>
            </div>
            {users && (users.length === 0 ? <p className="text-white/40 text-sm">No matches.</p> :
              <div className="space-y-1" data-testid="admin-user-results">{users.map((u) => <div key={u.id} className="text-sm text-white/70 py-1.5 border-b border-[#2A3544] last:border-0">@{u.handle} · {u.email} {u.is_admin ? "· admin" : ""}</div>)}</div>)}
            <p className="text-[11px] text-white/35 mt-2">Read-only lookup. No private message or room content is shown here.</p>
          </Panel>
          <Panel title="MODERATION" icon={Users2}>
            <Link to="/moderation" data-testid="console-link-moderation" className="inline-flex items-center gap-2 px-4 h-10 rounded-lg tt-panel-3 text-white text-sm hover:text-white"><ExternalLink size={15} /> Open moderation queue &amp; reports</Link>
          </Panel>
        </>
      )}

      {section === "commerce" && (
        <Panel title="COMMERCE · SANDBOX" icon={ShoppingBag}>
          <div className="rounded-lg border border-[#2A3544] bg-black/20 px-3 py-2.5 text-[12px] text-white/60" data-testid="commerce-sandbox-note">
            Commerce runs in <span className="text-[#F4B740]">SANDBOX</span> mode — no live charges. Entitlements and orders shown across the app are sandbox records.
          </div>
          <Link to="/shop" data-testid="console-link-shop" className="mt-3 inline-flex items-center gap-2 px-4 h-10 rounded-lg tt-panel-3 text-white text-sm"><ExternalLink size={15} /> Open Shop</Link>
        </Panel>
      )}

      {section === "telemetry" && <AcquisitionPanel />}
    </div>
  );
}
